import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and } from 'ember-truth-helpers';

export interface AttemptEntry {
  attemptNumber: number | null;
  status: string;
  timestamp: string;
}

export interface CascadeSubstep {
  label: string;
  status: string;
  timestamp?: string;
  isActive?: boolean;
  entryId?: number;
  attempts?: AttemptEntry[];
}

interface ToneStyle {
  bg: string;
  ring: string;
  badge: string;
  label: string;
}

interface EnrichedAttempt extends AttemptEntry {
  tone: ToneStyle;
}

interface EnrichedSubstep extends CascadeSubstep {
  tone: ToneStyle;
  idx: number;
  hasMultipleAttempts: boolean;
  isExpanded: boolean;
  attemptTones: EnrichedAttempt[];
}

interface Signature {
  Args: {
    label: string;
    status: string;
    summary?: string; // "2 succeeded · 2 skipped" — shown on group header
    timestamp?: string; // "08:23:10 CST" — shown on leaf row
    substeps?: CascadeSubstep[];
    isActive?: boolean;
    defaultOpen?: boolean;
    onClick?: () => void;
    onSelectSubstep?: (id: number) => void;
  };
  Element: HTMLDivElement;
}

function toneFor(status: string): ToneStyle {
  const s = (status ?? '').toUpperCase();
  if (s === 'SUCCESS' || s === 'SUCCEEDED') {
    return {
      bg: 'bg-cilantro-50',
      ring: 'ring-cilantro-50',
      badge: 'bg-cilantro-10 text-cilantro-60',
      label: 'Success',
    };
  } else if (s === 'FAILURE' || s === 'FAILED') {
    return { bg: 'bg-papaya-50', ring: 'ring-papaya-50', badge: 'bg-papaya-10 text-papaya-60', label: 'Failure' };
  } else if (s === 'PARTIAL' || s === 'PARTIAL_FAILURE') {
    return { bg: 'bg-amber-500', ring: 'ring-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'Partial' };
  } else if (s.startsWith('SKIPPED')) {
    return { bg: 'bg-carbon-30', ring: 'ring-carbon-30', badge: 'bg-gray-100 text-carbon-40', label: 'Skipped' };
  } else if (s === 'CANCELLED') {
    return { bg: 'bg-carbon-30', ring: 'ring-carbon-30', badge: 'bg-gray-100 text-carbon-40', label: 'Cancelled' };
  } else {
    return { bg: 'bg-carbon-30', ring: 'ring-carbon-30', badge: 'bg-gray-100 text-carbon-40', label: status ?? '—' };
  }
}

export default class CascadeStepGroup extends Component<Signature> {
  @tracked isOpen = this.args.defaultOpen ?? false;
  @tracked expandedSubstepIndices = new Set<number>();

  @action toggle(): void {
    this.isOpen = !this.isOpen;
  }

  @action onSubstepSelect(step: EnrichedSubstep): void {
    if (step.entryId !== undefined) this.args.onSelectSubstep?.(step.entryId);
  }

  @action toggleSubstep(idx: number): void {
    const next = new Set(this.expandedSubstepIndices);
    if (next.has(idx)) {
      next.delete(idx);
    } else {
      next.add(idx);
    }
    this.expandedSubstepIndices = next;
  }

  get hasChildren(): boolean {
    return (this.args.substeps?.length ?? 0) > 0;
  }

  get tone(): ToneStyle {
    return toneFor(this.args.status);
  }

  get enrichedSubsteps(): EnrichedSubstep[] {
    const expanded = this.expandedSubstepIndices;
    return (this.args.substeps ?? []).map((s, idx) => {
      const attempts = s.attempts ?? [];
      return {
        ...s,
        tone: toneFor(s.status),
        idx,
        hasMultipleAttempts: attempts.length > 1,
        isExpanded: expanded.has(idx),
        attemptTones: attempts.map((a) => ({ ...a, tone: toneFor(a.status) })),
      };
    });
  }

  <template>
    <div
      class="rounded-xl border overflow-hidden {{if @isActive 'border-gem-40 bg-gem-5' 'border-carbon-10'}}"
      ...attributes
    >
      {{#if this.hasChildren}}
        {{! ── Group header (expandable) ── }}
        <button
          type="button"
          class="flex items-center gap-2.5 w-full px-5 py-3 text-left transition-colors focus:outline-none
            {{if @isActive 'bg-gem-5 hover:bg-gem-10' 'bg-carbon-5 hover:bg-carbon-10'}}"
          {{on "click" this.toggle}}
        >
          <span class="w-3 h-3 shrink-0 flex items-center justify-center text-carbon-50">
            {{svgJar
              "carot-right"
              height="10"
              width="10"
              class=(if this.isOpen "rotate-90 transition-transform duration-150" "transition-transform duration-150")
            }}
          </span>
          <span
            class="w-4 h-4 rounded-full outline outline-1 bg-transparent shrink-0 flex items-center justify-center
              {{this.tone.ring}}"
          >
            <span class="rounded-full {{this.tone.bg}}" style="width:13px;height:13px;"></span>
          </span>
          <span class="text-sm font-bold text-carbon-90">{{@label}}</span>
          <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full {{this.tone.badge}}">
            {{this.tone.label}}
          </span>
          {{#if @summary}}
            <span class="ml-auto text-xs text-carbon-50 whitespace-nowrap pl-2">{{@summary}}</span>
          {{/if}}
        </button>

        {{! ── Substeps (shown when expanded) ── }}
        {{#if this.isOpen}}
          <div class="flex flex-col">
            {{#each this.enrichedSubsteps as |step|}}
              <div class="border-t border-carbon-10 {{if step.isActive 'bg-gem-5'}}">
                {{! Leaf row }}
                {{#if (and @onSelectSubstep step.entryId)}}
                  <button
                    type="button"
                    class="flex items-center gap-2.5 w-full px-5 py-2.5 text-left transition-colors focus:outline-none
                      {{if step.isActive 'bg-gem-5 hover:bg-gem-10' 'hover:bg-carbon-5'}}"
                    {{on "click" (fn this.onSubstepSelect step)}}
                  >
                    <span class="w-3 h-3 shrink-0" aria-hidden="true"></span>
                    <span
                      class="w-4 h-4 rounded-full outline outline-1 bg-transparent shrink-0 flex items-center justify-center
                        {{step.tone.ring}}"
                    >
                      <span class="rounded-full {{step.tone.bg}}" style="width:10px;height:10px;"></span>
                    </span>
                    <span class="text-sm font-semibold text-carbon-80">{{step.label}}</span>
                    <span
                      class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full {{step.tone.badge}}"
                    >
                      {{step.tone.label}}
                    </span>
                    {{#if step.hasMultipleAttempts}}
                      <span
                        class="ml-auto flex items-center gap-1 text-xs text-carbon-50 tabular-nums pl-2"
                        {{on "click" (fn this.toggleSubstep step.idx)}}
                      >
                        {{svgJar
                          "carot-right"
                          height="8"
                          width="8"
                          class=(if
                            step.isExpanded
                            "rotate-90 transition-transform duration-150"
                            "transition-transform duration-150"
                          )
                        }}
                        {{step.attemptTones.length}}
                        attempts
                      </span>
                    {{else if step.timestamp}}
                      <span class="ml-auto text-xs text-carbon-50 tabular-nums pl-2">{{step.timestamp}}</span>
                    {{/if}}
                  </button>
                {{else}}
                  <div class="flex items-center gap-2.5 px-5 py-2.5">
                    <span class="w-3 h-3 shrink-0" aria-hidden="true"></span>
                    <span
                      class="w-4 h-4 rounded-full outline outline-1 bg-transparent shrink-0 flex items-center justify-center
                        {{step.tone.ring}}"
                    >
                      <span class="rounded-full {{step.tone.bg}}" style="width:10px;height:10px;"></span>
                    </span>
                    <span class="text-sm font-semibold text-carbon-80">{{step.label}}</span>
                    <span
                      class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full {{step.tone.badge}}"
                    >
                      {{step.tone.label}}
                    </span>
                    {{#if step.hasMultipleAttempts}}
                      <button
                        type="button"
                        class="ml-auto flex items-center gap-1 text-xs text-carbon-50 hover:text-carbon-70 tabular-nums pl-2 focus:outline-none"
                        {{on "click" (fn this.toggleSubstep step.idx)}}
                      >
                        {{svgJar
                          "carot-right"
                          height="8"
                          width="8"
                          class=(if
                            step.isExpanded
                            "rotate-90 transition-transform duration-150"
                            "transition-transform duration-150"
                          )
                        }}
                        {{step.attemptTones.length}}
                        attempts
                      </button>
                    {{else if step.timestamp}}
                      <span class="ml-auto text-xs text-carbon-50 tabular-nums pl-2">{{step.timestamp}}</span>
                    {{/if}}
                  </div>
                {{/if}}
                {{! Attempt rows (nested, most recent first) }}
                {{#if step.isExpanded}}
                  <div class="flex flex-col">
                    {{#each step.attemptTones as |attempt|}}
                      <div class="flex items-center gap-2.5 px-5 py-1.5 border-t border-carbon-10 bg-carbon-5">
                        <span class="w-3 h-3 shrink-0" aria-hidden="true"></span>
                        <span class="w-4 h-4 shrink-0" aria-hidden="true"></span>
                        <span class="text-xs text-carbon-50 tabular-nums w-20 shrink-0">
                          {{#if attempt.attemptNumber}}Attempt {{attempt.attemptNumber}}{{else}}Attempt{{/if}}
                        </span>
                        <span
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full
                            {{attempt.tone.badge}}"
                        >
                          {{attempt.tone.label}}
                        </span>
                        {{#if attempt.timestamp}}
                          <span class="ml-auto text-xs text-carbon-50 tabular-nums pl-2">{{attempt.timestamp}}</span>
                        {{/if}}
                      </div>
                    {{/each}}
                  </div>
                {{/if}}
              </div>
            {{/each}}
          </div>
        {{/if}}

      {{else}}
        {{! ── Leaf row (no children) ── }}
        {{#if @onClick}}
          <button
            type="button"
            class="flex items-center gap-2.5 w-full px-5 py-3 text-left transition-colors focus:outline-none
              {{if @isActive 'bg-gem-5 hover:bg-gem-10' 'bg-white hover:bg-carbon-5'}}"
            {{on "click" @onClick}}
          >
            <span class="w-3 h-3 shrink-0" aria-hidden="true"></span>
            <span
              class="w-4 h-4 rounded-full outline outline-1 bg-transparent shrink-0 flex items-center justify-center
                {{this.tone.ring}}"
            >
              <span class="rounded-full {{this.tone.bg}}" style="width:10px;height:10px;"></span>
            </span>
            <span class="text-sm font-bold text-carbon-90">{{@label}}</span>
            <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full {{this.tone.badge}}">
              {{this.tone.label}}
            </span>
            {{#if @timestamp}}
              <span class="ml-auto text-xs text-carbon-50 tabular-nums pl-2">{{@timestamp}}</span>
            {{/if}}
          </button>
        {{else}}
          <div class="flex items-center gap-2.5 px-5 py-3 {{if @isActive 'bg-gem-5'}}">
            <span class="w-3 h-3 shrink-0" aria-hidden="true"></span>
            <span
              class="w-4 h-4 rounded-full outline outline-1 bg-transparent shrink-0 flex items-center justify-center
                {{this.tone.ring}}"
            >
              <span class="rounded-full {{this.tone.bg}}" style="width:10px;height:10px;"></span>
            </span>
            <span class="text-sm font-bold text-carbon-90">{{@label}}</span>
            <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full {{this.tone.badge}}">
              {{this.tone.label}}
            </span>
            {{#if @timestamp}}
              <span class="ml-auto text-xs text-carbon-50 tabular-nums pl-2">{{@timestamp}}</span>
            {{/if}}
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
